import { useId } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';

import { useAccessibilityPreferencesContext } from '@/lib/accessibility/accessibility-preferences-context';
import { accessibilityMessages } from '@/lib/accessibility/messages';
import type { AccessibilityPreferences } from '@/lib/accessibility/types';

interface AccessibilityPreferencesPanelProps {
  open: boolean;
  onClose: () => void;
}

const TEXT_SIZE_OPTIONS: { value: AccessibilityPreferences['textSize']; label: string }[] = [
  { value: 'default', label: accessibilityMessages.textSizeDefault },
  { value: 'large', label: accessibilityMessages.textSizeLarge },
  { value: 'extra-large', label: accessibilityMessages.textSizeExtraLarge },
];

const TEXT_SPACING_OPTIONS: { value: AccessibilityPreferences['textSpacing']; label: string }[] = [
  { value: 'default', label: accessibilityMessages.textSpacingDefault },
  { value: 'increased', label: accessibilityMessages.textSpacingIncreased },
  { value: 'maximum', label: accessibilityMessages.textSpacingMaximum },
];

const CONTENT_SPACING_OPTIONS: { value: AccessibilityPreferences['contentSpacing']; label: string }[] = [
  { value: 'default', label: accessibilityMessages.contentSpacingDefault },
  { value: 'comfortable', label: accessibilityMessages.contentSpacingComfortable },
  { value: 'expanded', label: accessibilityMessages.contentSpacingExpanded },
];

const CONTRAST_OPTIONS: { value: AccessibilityPreferences['contrast']; label: string }[] = [
  { value: 'default', label: accessibilityMessages.contrastDefault },
  { value: 'high-contrast-light', label: accessibilityMessages.contrastHighLight },
  { value: 'high-contrast-dark', label: accessibilityMessages.contrastHighDark },
];

const BOOLEAN_PREFERENCES: {
  key: Extract<keyof AccessibilityPreferences, 'readableFont' | 'highlightLinks' | 'enhancedFocus' | 'reduceMotion' | 'pauseAnimations'>;
  label: string;
  description: string;
}[] = [
  { key: 'readableFont', label: accessibilityMessages.readableFont, description: accessibilityMessages.readableFontDescription },
  { key: 'highlightLinks', label: accessibilityMessages.highlightLinks, description: accessibilityMessages.highlightLinksDescription },
  { key: 'enhancedFocus', label: accessibilityMessages.enhancedFocus, description: accessibilityMessages.enhancedFocusDescription },
  { key: 'reduceMotion', label: accessibilityMessages.reduceMotion, description: accessibilityMessages.reduceMotionDescription },
  { key: 'pauseAnimations', label: accessibilityMessages.pauseAnimations, description: accessibilityMessages.pauseAnimationsDescription },
];

function EnumControl<K extends keyof AccessibilityPreferences>({
  legend,
  groupKey,
  options,
  value,
  onChange,
}: {
  legend: string;
  groupKey: K;
  options: { value: AccessibilityPreferences[K]; label: string }[];
  value: AccessibilityPreferences[K];
  onChange: (key: K, value: AccessibilityPreferences[K]) => void;
}) {
  const labelId = useId();

  return (
    <FormControl component="fieldset">
      <FormLabel id={labelId} component="legend" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {legend}
      </FormLabel>
      <RadioGroup
        aria-labelledby={labelId}
        value={value}
        onChange={(event) => onChange(groupKey, event.target.value as AccessibilityPreferences[K])}
      >
        {options.map((option) => (
          <FormControlLabel
            key={String(option.value)}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

export function AccessibilityPreferencesPanel({ open, onClose }: AccessibilityPreferencesPanelProps) {
  const titleId = useId();
  const descriptionId = useId();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { preferences, setPreference, reset } = useAccessibilityPreferencesContext();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <Stack direction="row" sx={{ alignItems: 'center', pl: 3, pr: 1.5, py: 1.5 }}>
        <DialogTitle id={titleId} sx={{ p: 0, flex: 1, fontWeight: 700 }}>
          {accessibilityMessages.panelTitle}
        </DialogTitle>
        <IconButton onClick={onClose} aria-label={accessibilityMessages.closePanel}>
          <XIcon size={20} aria-hidden />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography id={descriptionId} variant="body2" color="text.secondary">
            {accessibilityMessages.panelDescription}
          </Typography>

          <EnumControl
            legend={accessibilityMessages.textSize}
            groupKey="textSize"
            options={TEXT_SIZE_OPTIONS}
            value={preferences.textSize}
            onChange={setPreference}
          />
          <EnumControl
            legend={accessibilityMessages.textSpacing}
            groupKey="textSpacing"
            options={TEXT_SPACING_OPTIONS}
            value={preferences.textSpacing}
            onChange={setPreference}
          />
          <EnumControl
            legend={accessibilityMessages.contentSpacing}
            groupKey="contentSpacing"
            options={CONTENT_SPACING_OPTIONS}
            value={preferences.contentSpacing}
            onChange={setPreference}
          />
          <EnumControl
            legend={accessibilityMessages.contrast}
            groupKey="contrast"
            options={CONTRAST_OPTIONS}
            value={preferences.contrast}
            onChange={setPreference}
          />

          <Divider />

          <Stack spacing={1.5}>
            {BOOLEAN_PREFERENCES.map(({ key, label, description }) => (
              <Stack key={key} spacing={0}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences[key]}
                      onChange={(event) => setPreference(key, event.target.checked)}
                    />
                  }
                  label={label}
                />
                <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                  {description}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={reset} color="inherit">
          {accessibilityMessages.reset}
        </Button>
        <Button onClick={onClose} variant="contained">
          {accessibilityMessages.done}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
